import React from 'react'
import Dialog from './dialog'
import Button from './button'
import { AlertTriangle } from 'lucide-react'

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'destructive'
  onConfirm: () => void | Promise<void>
  loading?: boolean
  suppressKey?: string
  dontShowAgainSelected?: boolean
  onToggleDontShowAgain?: (value: boolean) => void
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  onConfirm,
  loading = false,
  suppressKey,
  dontShowAgainSelected,
  onToggleDontShowAgain,
}) => {
  const [isConfirming, setIsConfirming] = React.useState(false)

  const handleConfirm = async () => {
    if (isConfirming) return
    setIsConfirming(true)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch (error) {
      console.error('Action failed:', error)
      // Keep dialog open on error so user can retry
    } finally {
      setIsConfirming(false)
    }
  }

  const handleCancel = () => {
    if (isConfirming) return
    onOpenChange(false)
  }

  const isLoading = loading || isConfirming

  return (
    <Dialog 
      open={open} 
      onOpenChange={onOpenChange} 
      size="sm"
      heightMode="auto"
      className="max-w-md"
    >
      <div className="flex flex-col items-center text-center space-y-4">
        {variant === 'destructive' && (
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
        )}
        
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">
            {title}
          </h3>
          {description && (
            <p className="text-sm text-foreground/60">
              {description}
            </p>
          )}
        </div>

        <div className="flex w-full gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1"
          >
            {cancelText}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'danger' : 'default'}
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? 'Processing...' : confirmText}
          </Button>
        </div>

        {suppressKey && onToggleDontShowAgain && (
          <label className="mt-2 flex items-center gap-2 w-full text-left cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-black/20 accent-brand-600"
              checked={!!dontShowAgainSelected}
              onChange={(e) => onToggleDontShowAgain(e.target.checked)}
            />
            <span className="text-xs text-foreground/70">Don't show this again</span>
          </label>
        )}
      </div>
    </Dialog>
  )
}

export default ConfirmDialog
